(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const decodeWizardCartProperties = (encoded) => {
    if (!encoded || encoded.length > 24000 || !/^[A-Za-z0-9_-]+$/.test(encoded)) {
      throw new Error('The design details are invalid.');
    }

    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    const properties = JSON.parse(decoded);

    if (!properties || Array.isArray(properties) || typeof properties !== 'object') {
      throw new Error('The design details are invalid.');
    }

    return Object.fromEntries(
      Object.entries(properties)
        .filter(([key, value]) => key && value !== null && value !== undefined)
        .map(([key, value]) => [String(key), String(value)])
    );
  };

  const createWizardCartStatus = () => {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-mystic-wizard-cart-status', '');
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483647',
      display: 'grid',
      placeItems: 'center',
      padding: '24px',
      background: '#02070b',
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      textAlign: 'center',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(100%, 520px)',
      padding: '32px',
      border: '1px solid rgba(0, 174, 239, 0.45)',
      borderRadius: '16px',
      background: '#07141d',
    });

    const title = document.createElement('h1');
    title.textContent = 'Adding your design to the cart';
    Object.assign(title.style, { margin: '0 0 12px', fontSize: '26px', color: '#00aeef' });

    const message = document.createElement('p');
    message.textContent = 'Please wait. Your existing cart items will stay in the cart.';
    Object.assign(message.style, { margin: '0', fontSize: '16px', lineHeight: '1.5' });

    const actions = document.createElement('div');
    Object.assign(actions.style, {
      display: 'none',
      justifyContent: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      marginTop: '22px',
    });

    card.append(title, message, actions);
    overlay.append(card);
    document.body.append(overlay);

    return { overlay, title, message, actions };
  };

  const handleWizardCartHandoff = async () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mystic_wizard_cart') !== '1') return;
    if (document.documentElement.dataset.mysticWizardCartPending === 'true') return;
    document.documentElement.dataset.mysticWizardCartPending = 'true';

    const status = createWizardCartStatus();

    try {
      const variantId = params.get('variant') || '';
      const quantity = Number.parseInt(params.get('quantity') || '1', 10);
      if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 999) {
        throw new Error('The selected mat option is invalid.');
      }

      const properties = decodeWizardCartProperties(params.get('properties') || '');
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }],
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Shopify could not add this design.');
      }

      status.title.textContent = 'Design added';
      status.message.textContent = 'Opening your cart…';
      const cartUrl = window.routes?.cart_url || `${root}cart`;
      window.location.replace(cartUrl);
    } catch (error) {
      document.documentElement.dataset.mysticWizardCartPending = 'false';
      status.overlay.setAttribute('role', 'alert');
      status.title.textContent = 'Could not add this design';
      status.title.style.color = '#ff7b7b';
      status.message.textContent = `${error.message || 'Please try again.'} Your existing cart was not changed.`;
      status.actions.style.display = 'flex';

      const retryButton = document.createElement('button');
      retryButton.type = 'button';
      retryButton.textContent = 'Try again';
      Object.assign(retryButton.style, {
        border: '0',
        borderRadius: '8px',
        padding: '12px 18px',
        background: '#00aeef',
        color: '#001018',
        fontWeight: '700',
        cursor: 'pointer',
      });
      retryButton.addEventListener('click', () => window.location.reload());

      const wizardLink = document.createElement('a');
      wizardLink.href = 'https://mysticmatsusa.app/dashboard.php';
      wizardLink.textContent = 'Return to Design Wizard';
      Object.assign(wizardLink.style, {
        border: '1px solid rgba(255, 255, 255, 0.35)',
        borderRadius: '8px',
        padding: '12px 18px',
        color: '#ffffff',
        textDecoration: 'none',
        fontWeight: '700',
      });

      status.actions.replaceChildren(retryButton, wizardLink);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void handleWizardCartHandoff(), { once: true });
  } else {
    void handleWizardCartHandoff();
  }

  class MysticSiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.desktopDropdown = this.querySelector('.mystic-nav-dropdown');
      this.desktopTrigger = this.querySelector('.mystic-nav-dropdown__trigger');
      this.desktopMenu = this.querySelector('.mystic-nav-dropdown__menu');
      this.mobileOpenButton = this.querySelector('[data-mystic-menu-open]');
      this.mobileDrawer = this.querySelector('.mystic-mobile-menu');
      this.mobileOverlay = this.querySelector('.mystic-menu-overlay');
      this.mobileSubmenuTrigger = this.querySelector('.mystic-mobile-menu__submenu-trigger');
      this.mobileSubmenu = this.querySelector('.mystic-mobile-menu__submenu');
      this.lastFocus = null;

      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onResize = this.handleResize.bind(this);
      document.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      document.body.classList.remove('mystic-menu-lock');
    }

    setDesktopDropdown(open, restoreFocus = false) {
      if (!this.desktopDropdown || !this.desktopTrigger || !this.desktopMenu) return;
      this.desktopDropdown.classList.toggle('is-open', open);
      this.desktopTrigger.setAttribute('aria-expanded', String(open));
      this.desktopMenu.setAttribute('aria-hidden', String(!open));
      if (!open && restoreFocus) this.desktopTrigger.focus();
    }

    setMobileSubmenu(open) {
      if (!this.mobileSubmenuTrigger || !this.mobileSubmenu) return;
      this.mobileSubmenuTrigger.setAttribute('aria-expanded', String(open));
      this.mobileSubmenu.classList.toggle('is-open', open);
      this.mobileSubmenu.setAttribute('aria-hidden', String(!open));
    }

    openMobileMenu() {
      if (!this.mobileDrawer || !this.mobileOverlay) return;
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.classList.contains('active') && typeof cartDrawer.close === 'function') cartDrawer.close();

      this.lastFocus = document.activeElement;
      this.setDesktopDropdown(false);
      this.setMobileSubmenu(false);
      this.mobileDrawer.classList.add('is-open');
      this.mobileOverlay.classList.add('is-open');
      this.mobileDrawer.setAttribute('aria-hidden', 'false');
      this.mobileDrawer.inert = false;
      this.mobileOpenButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mystic-menu-lock');
      window.setTimeout(() => this.mobileDrawer.querySelector('[data-mystic-menu-close]')?.focus(), 50);
    }

    closeMobileMenu(restoreFocus = true) {
      if (!this.mobileDrawer || !this.mobileOverlay) return;
      this.mobileDrawer.classList.remove('is-open');
      this.mobileOverlay.classList.remove('is-open');
      this.mobileDrawer.setAttribute('aria-hidden', 'true');
      this.mobileDrawer.inert = true;
      this.mobileOpenButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mystic-menu-lock');
      this.setMobileSubmenu(false);
      if (restoreFocus && this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    handleClick(event) {
      if (this.desktopTrigger?.contains(event.target)) {
        event.preventDefault();
        this.setDesktopDropdown(!this.desktopDropdown.classList.contains('is-open'));
        return;
      }

      if (this.mobileOpenButton?.contains(event.target)) {
        event.preventDefault();
        this.openMobileMenu();
        return;
      }

      if (event.target.closest('[data-mystic-menu-close]') && this.contains(event.target)) {
        event.preventDefault();
        this.closeMobileMenu(true);
        return;
      }

      if (this.mobileSubmenuTrigger?.contains(event.target)) {
        event.preventDefault();
        this.setMobileSubmenu(this.mobileSubmenuTrigger.getAttribute('aria-expanded') !== 'true');
        return;
      }

      if (event.target.closest('.mystic-mobile-menu__nav a') && this.contains(event.target)) {
        this.closeMobileMenu(false);
      }

      if (this.desktopDropdown && !this.desktopDropdown.contains(event.target)) {
        this.setDesktopDropdown(false);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.mobileDrawer?.classList.contains('is-open')) {
          event.preventDefault();
          this.closeMobileMenu(true);
          return;
        }
        if (this.desktopDropdown?.classList.contains('is-open')) {
          event.preventDefault();
          this.setDesktopDropdown(false, true);
        }
        return;
      }

      if (event.key !== 'Tab' || !this.mobileDrawer?.classList.contains('is-open')) return;
      const focusable = [...this.mobileDrawer.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleResize() {
      if (window.innerWidth >= 991) this.closeMobileMenu(false);
    }
  }

  class MysticHeroSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = [...this.querySelectorAll('[data-mystic-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-mystic-hero-dot]')];
      this.current = 0;
      this.pointerStartX = null;
      this.timer = null;
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.delay = Math.max(2500, Number(this.dataset.autoplayDelay || 4600));
      if (!this.slides.length) return;

      this.slides.forEach((slide) => slide.querySelectorAll('img').forEach((image) => { image.draggable = false; }));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.mysticHeroDot || 0), true)));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('pointerdown', (event) => { this.pointerStartX = event.clientX; });
      this.addEventListener('pointerup', (event) => {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) >= 45) this.show(this.current + (distance < 0 ? 1 : -1), true);
      });
      this.addEventListener('pointercancel', () => { this.pointerStartX = null; });
      this.onVisibility = () => document.hidden ? this.stop() : this.start();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.show(0, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    show(index, restart) {
      this.current = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.current;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (restart) this.start();
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    start() {
      this.stop();
      if (this.slides.length < 2 || this.reducedMotion || document.hidden) return;
      this.timer = window.setInterval(() => this.show(this.current + 1, false), this.delay);
    }
  }

  class MysticDragStrip extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.activePointerId = null;
      this.moved = false;
      this.suppressClick = false;
      this.addEventListener('pointerdown', this.pointerDown.bind(this));
      this.addEventListener('pointermove', this.pointerMove.bind(this));
      this.addEventListener('pointerup', this.finishDrag.bind(this));
      this.addEventListener('pointercancel', this.finishDrag.bind(this));
      this.addEventListener('lostpointercapture', this.finishDrag.bind(this));
      this.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    }

    pointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.scrollWidth <= this.clientWidth) return;
      this.activePointerId = event.pointerId;
      this.startX = event.clientX;
      this.initialScrollLeft = this.scrollLeft;
      this.moved = false;
      this.classList.add('is-dragging');
      this.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    pointerMove(event) {
      if (event.pointerId !== this.activePointerId) return;
      const distance = (event.clientX - this.startX) * 1.25;
      if (Math.abs(distance) > 4) this.moved = true;
      this.scrollLeft = this.initialScrollLeft - distance;
      event.preventDefault();
    }

    finishDrag(event) {
      if (this.activePointerId === null || (typeof event.pointerId === 'number' && event.pointerId !== this.activePointerId)) return;
      const pointerId = this.activePointerId;
      this.activePointerId = null;
      this.classList.remove('is-dragging');
      if (this.hasPointerCapture?.(pointerId)) this.releasePointerCapture(pointerId);
      if (this.moved) {
        this.suppressClick = true;
        window.setTimeout(() => { this.suppressClick = false; }, 0);
      }
      this.moved = false;
    }
  }

  class MysticProductFeatures extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.image = this.querySelector('[data-feature-product-image]');
      this.controls = [...this.querySelectorAll('[data-feature-control]')];
      this.switchTimer = null;
      if (!this.image || !this.controls.length) return;

      this.controls.forEach((control) => {
        control.addEventListener('mouseenter', () => this.select(control));
        control.addEventListener('focus', () => this.select(control));
        control.addEventListener('click', () => this.select(control));
        control.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.select(control);
        });
      });
    }

    disconnectedCallback() {
      window.clearTimeout(this.switchTimer);
    }

    select(control) {
      const source = control.dataset.featureImageSrc;
      if (!source || control.classList.contains('is-featured')) return;

      this.controls.forEach((item) => {
        const active = item === control;
        item.classList.toggle('is-featured', active);
        item.setAttribute('aria-pressed', String(active));
      });

      window.clearTimeout(this.switchTimer);
      this.image.classList.add('is-switching');
      this.switchTimer = window.setTimeout(() => {
        this.image.src = source;
        this.image.alt = control.dataset.featureImageAlt || '';
        this.image.classList.remove('is-switching');
      }, 150);
    }
  }

  class MysticFaqFilter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.filters = [...this.querySelectorAll('[data-faq-filter]')];
      this.items = [...this.querySelectorAll('[data-faq-item]')];
      this.filters.forEach((filter) => filter.addEventListener('click', () => this.select(filter)));
    }

    select(selectedFilter) {
      const category = selectedFilter.dataset.faqFilter;
      this.filters.forEach((filter) => {
        const active = filter === selectedFilter;
        filter.classList.toggle('is-active', active);
        filter.setAttribute('aria-pressed', String(active));
      });
      this.items.forEach((item) => {
        const visible = category === 'all' || item.dataset.faqCategory === category;
        item.hidden = !visible;
        item.classList.toggle('is-hidden', !visible);
        if (!visible) item.open = false;
      });
    }
  }

  class MysticProductAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.checkbox = this.querySelector('[data-mystic-addon-checkbox]');
      this.error = this.querySelector('[data-mystic-addon-error]');
      this.form = document.getElementById(this.dataset.formId);
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.pending = false;
      if (!this.checkbox || !this.form || !this.dataset.variantId) return;

      this.onSubmit = () => {
        this.pending = this.checkbox.checked && !this.checkbox.disabled;
        this.showError();
      };
      this.form.addEventListener('submit', this.onSubmit, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleCartUpdate(event));
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event?.source === 'product-form') this.pending = false;
        });
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.cartUpdateUnsubscriber?.();
      this.cartErrorUnsubscriber?.();
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async handleCartUpdate(event) {
      if (event?.source !== 'product-form' || !this.pending) return;
      this.pending = false;

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData();
        formData.append('id', this.dataset.variantId);
        formData.append('quantity', '1');
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        const response = await fetch(window.routes.cart_add_url, config);
        const addonCartData = await response.json();
        if (!response.ok || addonCartData.status) {
          throw new Error(addonCartData.description || addonCartData.message || 'The add-on could not be added.');
        }

        if (event.cartData && typeof event.cartData === 'object') {
          Object.keys(event.cartData).forEach((key) => delete event.cartData[key]);
          Object.assign(event.cartData, addonCartData);
        }
        this.showError();
      } catch (error) {
        this.showError(error.message || 'The mat was added, but the optional add-on could not be added.');
      }
    }
  }

  if (!customElements.get('mystic-site-header')) customElements.define('mystic-site-header', MysticSiteHeader);
  if (!customElements.get('mystic-hero-slider')) customElements.define('mystic-hero-slider', MysticHeroSlider);
  if (!customElements.get('mystic-drag-strip')) customElements.define('mystic-drag-strip', MysticDragStrip);
  if (!customElements.get('mystic-product-features')) customElements.define('mystic-product-features', MysticProductFeatures);
  if (!customElements.get('mystic-faq-filter')) customElements.define('mystic-faq-filter', MysticFaqFilter);
  if (!customElements.get('mystic-product-addon')) customElements.define('mystic-product-addon', MysticProductAddon);

  const setPlaybackRate = (root = document) => {
    root.querySelectorAll('video[data-mystic-playback-rate], [data-mystic-video-rate] video').forEach((video) => {
      const rateSource = video.dataset.mysticPlaybackRate || video.closest('[data-mystic-video-rate]')?.dataset.mysticVideoRate || 1;
      const rate = Number(rateSource);
      if (!Number.isFinite(rate) || rate <= 0) return;
      const apply = () => {
        video.defaultPlaybackRate = rate;
        video.playbackRate = rate;
      };
      apply();
      video.addEventListener('loadedmetadata', apply, { once: true });
    });
  };

  setPlaybackRate();
  document.addEventListener('shopify:section:load', (event) => setPlaybackRate(event.target));

  // Keep Wizard visits in a top-level tab and replace old login-gated CTAs
  // with the guest entry. Account login and synchronized logout stay separate.
  const wizardOrigin = 'https://mysticmatsusa.app';
  const isWizardDestination = (link) => {
    if (!(link instanceof HTMLAnchorElement) || !link.href) return false;
    try {
      const destination = new URL(link.href, window.location.href);
      return destination.origin === wizardOrigin && !destination.pathname.endsWith('/logout.php');
    } catch (error) {
      return false;
    }
  };

  const prepareWizardLink = (link) => {
    if (!isWizardDestination(link)) return;
    const destination = new URL(link.href, window.location.href);
    const storefrontSignedIn = document.querySelector('[data-mystic-storefront-customer="true"]');
    if (!storefrontSignedIn && destination.pathname.endsWith('/shopify-login.php')
        && (!destination.searchParams.get('return_to') || destination.searchParams.get('return_to') === 'dashboard.php')
        && !destination.searchParams.has('verify') && !destination.searchParams.has('interactive')) {
      destination.pathname = '/index.php';
      destination.search = '';
      link.href = destination.href;
    }
    link.target = '_blank';
    link.rel = 'noopener';
  };

  const prepareWizardLinks = (root = document) => {
    root.querySelectorAll('a[href]').forEach((link) => {
      prepareWizardLink(link);
    });
  };

  prepareWizardLinks();
  document.addEventListener('shopify:section:load', (event) => prepareWizardLinks(event.target));
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    prepareWizardLink(link);
  }, true);

  const closeAccountMenus = (except = null) => {
    document.querySelectorAll('[data-mystic-account-menu][open]').forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
    });
  };

  document.addEventListener('click', (event) => {
    const activeMenu = event.target.closest?.('[data-mystic-account-menu]') || null;
    closeAccountMenus(activeMenu);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openMenu = document.querySelector('[data-mystic-account-menu][open]');
    if (!openMenu) return;
    openMenu.removeAttribute('open');
    openMenu.querySelector('summary')?.focus();
  });
})();
